import { Radio, RadioGroup, SelectItem, TmdxSelect, TmdxTextarea } from '@ds';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { SelectItemType } from '@/types';
import {
  cancelReasonsForDryRun,
  CaseTurndownOutcomes,
  DISPOSITION_COMMENT_MAX_LENGTH,
  getCaseDeclineOptions,
} from '../config';
import { DeclineCaseSection } from './DeclineCaseSection';
import { CaseStatus, DeclineCaseParams, OutcomeType } from '@/api/apis/case';

type DeclineCaseFormProps = {
  disabledOutcomes?: boolean;
  dispositionReasons: SelectItemType[];
  dispositionSpecifications: SelectItemType[];
  status: CaseStatus;
};

export const DeclineCaseForm = ({
  disabledOutcomes,
  dispositionReasons,
  dispositionSpecifications,
  status,
}: DeclineCaseFormProps) => {
  const { control } = useFormContext<DeclineCaseParams>();
  const declineReason = useWatch({ control, name: 'declineReason' });
  const dispositionReasonsOptions = declineReason === OutcomeType.DryRun ? cancelReasonsForDryRun : dispositionReasons;

  return (
    <div className="flex flex-col gap-6">
      <DeclineCaseSection heading="Outcome:">
        <Controller
          name="declineReason"
          control={control}
          render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              className="gap-8"
              isDisabled={disabledOutcomes}
              orientation="horizontal"
              value={value || ''}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            >
              {getCaseDeclineOptions(status).map((option) => (
                <Radio key={option.value} value={option.value as string} size="lg" isDisabled={option.disabled}>
                  {option.name}
                </Radio>
              ))}
            </RadioGroup>
          )}
        />
      </DeclineCaseSection>
      <DeclineCaseSection heading="Cancelation Reason:">
        <div className="flex justify-between gap-4">
          <div className="flex-[50%]">
            <Controller
              name="dispositionReason"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  label="Reason"
                  aria-label="Select disposition reason"
                  isDisabled={dispositionReasonsOptions.length === 1}
                  errorMessage={error?.message}
                  isInvalid={invalid}
                  selectedKeys={value ? [value] : undefined}
                  disallowEmptySelection
                  {...field}
                >
                  {dispositionReasonsOptions.map((reason) => (
                    <SelectItem key={reason.value}>{reason.name}</SelectItem>
                  ))}
                </TmdxSelect>
              )}
            />
          </div>
          <div className="flex-[50%]">
            {dispositionSpecifications.length > 1 && (
              <Controller
                name="dispositionSpecification"
                control={control}
                render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                  <TmdxSelect
                    aria-label="Select disposition specification"
                    label="Specification"
                    isDisabled={dispositionSpecifications.length === 1}
                    errorMessage={error?.message}
                    isInvalid={invalid}
                    selectedKeys={value ? [value] : undefined}
                    disallowEmptySelection
                    {...field}
                  >
                    {dispositionSpecifications.map((spec) => (
                      <SelectItem key={spec.value}>{spec.name}</SelectItem>
                    ))}
                  </TmdxSelect>
                )}
              />
            )}
          </div>
        </div>
      </DeclineCaseSection>
      {declineReason === OutcomeType.Turndown && (
        <DeclineCaseSection heading="Turndown Outcome:">
          <Controller
            name="turndownOutcome"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <TmdxSelect
                aria-label="Select turndown outcome"
                label="Turndown Outcome (Optional)"
                placeholder="Select turndown outcome"
                errorMessage={error?.message}
                isInvalid={invalid}
                selectedKeys={value ? [value] : []}
                {...field}
              >
                {CaseTurndownOutcomes.map((option) => (
                  <SelectItem key={option.value}>{option.name}</SelectItem>
                ))}
              </TmdxSelect>
            )}
          />
        </DeclineCaseSection>
      )}
      <DeclineCaseSection heading="Additional Comments:">
        <Controller
          name="dispositionComment"
          control={control}
          rules={{ required: true, maxLength: DISPOSITION_COMMENT_MAX_LENGTH }}
          render={({ field, fieldState: { invalid, error } }) => (
            <TmdxTextarea
              fullWidth
              disableAnimation
              minRows={6}
              maxLength={DISPOSITION_COMMENT_MAX_LENGTH}
              placeholder="Type here"
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            />
          )}
        />
      </DeclineCaseSection>
    </div>
  );
};
